/*!
 * drawConfig.js — what draw()'s arguments mean
 * © 2025 Rob Canning — GPLv3
 *
 * The decisions draw() makes that are only arithmetic: reading its arguments,
 * stepping colour and width along the trail, fading ink out, and holding the
 * line to its point budget. No DOM and no imports, so they can be tested
 * without a browser — the pen itself (draw.js) cannot be.
 */

// ─────────────────────────────────────────────────────────────
//  Configuration — pure, so the tests do not need a document
// ─────────────────────────────────────────────────────────────

const num = (v, fallback) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
};

/**
 * The cue's args (the parser's [{type, value}] list) as a settled config.
 * color and width accept a list, which steps along the trail: one entry per
 * `seg` beats, cycling. fade: and seg: are written in beats; `k` is seconds
 * per beat (secPerBeat() in beats.js, passed in to keep this file import-free),
 * and the config holds them in seconds.
 */
export function parseDrawConfig(args = [], k = 1) {
    const p = {};
    for (const a of args) if (a && a.type) p[a.type] = a.value;

    const list = (v) => (Array.isArray(v) ? v.filter(x => x !== undefined && x !== null) : (v === undefined || v === null || v === "" ? null : [v]));

    return {
        colors: list(p.color),                       // null → the element's own colour
        widths: (list(p.width) || [2]).map(w => num(w, 2)),
        dash: p.dash != null ? String(p.dash) : null,
        opacity: Math.min(1, Math.max(0, num(p.opacity, 1))),
        min: Math.max(0, num(p.min, 1.5)),           // world units between samples
        max: Math.max(0, Math.round(num(p.max, 0))), // points kept, 0 = no limit
        fade: Math.max(0, num(p.fade, 0) * k),       // seconds for ink to vanish, 0 = never
        seg: Math.max(0.05, num(p.seg, 2) * k),      // seconds per colour/width step
        // How far the object may move in one frame before the pen decides it
        // was PUT there rather than that it travelled. null = work it out.
        jump: (p.jump == null || p.jump === "") ? null : Math.max(0, num(p.jump, 0)),
        close: p.close === 1 || p.close === "1" || p.close === true,
        // "score" (default): ink stays where it was drawn. "playhead": ink is
        // held relative to the playhead, so as the score scrolls on, a moving
        // object — or a still one — streams a horizontal trail behind it.
        anchor: String(p.anchor ?? "score").replace(/^["']|["']$/g, "") === "playhead" ? "playhead" : "score",
        uid: p.uid != null ? String(p.uid) : null,
    };
}

/**
 * Lifting the pen.
 *
 * An o2p in loop mode reaches the end of its path and reappears at the start;
 * a trans loop does the same; so does a scrub, a rewind, or a page opening
 * under the object. In every one of those the object did not travel, it was
 * put somewhere — and a pen that joins the two positions draws a long straight
 * line across the work that is not part of anything the object did.
 *
 * There is no signal to read for this: the animation cues assign a transform,
 * they do not announce a discontinuity. What distinguishes a jump is that it
 * is far bigger than the steps the same pen has been taking, so that is the
 * test. The running mean adapts as the object speeds up and slows down, and
 * the floor keeps a slow pen from lifting over an ordinary acceleration.
 *
 * jump:<units> sets the distance outright; jump:0 turns lifting off, and the
 * pen draws the return leg like anything else.
 */
export const JUMP_FLOOR = 24;    // score units — below this, never a jump
export const JUMP_FACTOR = 8;    // ... or this many times the recent average

export function isJump(step, meanStep, jump = null) {
    if (jump === 0) return false;
    if (jump > 0) return step > jump;
    return step > Math.max(JUMP_FLOOR, JUMP_FACTOR * (meanStep || 0));
}

/** Which step of a stepping list we are on, `seg` seconds at a time. */
export function stepIndex(elapsedMs, segSeconds) {
    if (!(segSeconds > 0)) return 0;
    return Math.floor(Math.max(0, elapsedMs) / (segSeconds * 1000));
}

/** The i-th entry of a list, cycling. */
export function pickCycled(listOrValue, i) {
    if (listOrValue == null) return null;
    const arr = Array.isArray(listOrValue) ? listOrValue : [listOrValue];
    if (!arr.length) return null;
    return arr[((i % arr.length) + arr.length) % arr.length];
}

/**
 * How opaque a run is, given how long it has been on the page. fade:0 means
 * the ink stays. Returns 0 once it is spent, which is the signal to drop it.
 */
export function fadeOpacity(ageMs, fadeSeconds, base = 1) {
    if (!(fadeSeconds > 0)) return base;
    const left = 1 - ageMs / (fadeSeconds * 1000);
    return left <= 0 ? 0 : base * left;
}

/**
 * Drop points from the front until `max` remain. Returns how many were
 * removed, so the caller can tell whether anything needs repainting.
 */
export function trimToMax(runs, max) {
    if (!(max > 0)) return 0;
    let total = runs.reduce((n, r) => n + r.points.length, 0);
    let dropped = 0;
    while (total > max && runs.length) {
        const first = runs[0];
        const over = total - max;
        if (first.points.length <= over) {
            total -= first.points.length;
            dropped += first.points.length;
            runs.shift();
        } else {
            first.points.splice(0, over);
            total -= over;
            dropped += over;
        }
    }
    return dropped;
}
